import React, { useEffect, useState } from "react";
import { io } from "socket.io-client";
import MakeDraftPick from "../components/MakeDraftPick";
import { useParams, useNavigate } from "react-router-dom";

const SOCKET_URL =
  process.env.REACT_APP_API_URL?.replace("/api/", "") ||
  "https://snap-tournament.onrender.com";

export default function SnapDraftLive({ user }) {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [socket, setSocket] = useState(null);
  const [draftComplete, setDraftComplete] = useState(false);
  const [finalResults, setFinalResults] = useState(null);
  const [roomState, setRoomState] = useState({
    participants: [],
    draftedCards: [],
    availableCards: [],
    hotCards: [],
    currentUser: null,
  });

  // --- Connect to Socket.IO ---
  useEffect(() => {
    const s = io(SOCKET_URL, { transports: ["websocket"] });
    setSocket(s);

    // join the room with user info
    s.emit("joinRoom", { roomId, user });

    // handle room updates
    s.on("stateUpdated", (state) => setRoomState(state));

    s.on("userJoined", (joinedUser) => {
      setRoomState((prev) => {
        const already = prev.participants.find((p) => p.id === joinedUser.id);
        return already
          ? prev
          : { ...prev, participants: [...prev.participants, joinedUser] };
      });
    });

    return () => s.disconnect();
  }, [roomId, user]);

  useEffect(() => {
  if (!socket) return;

  // 🔥 When draft starts, load the hot cards
  socket.on("draftStarted", ({ hotCards }) => {
    console.log("🔥 Hot cards received:", hotCards);
    setRoomState((prev) => ({ ...prev, hotCards }));
  });

    // 🌀 When full state updates, keep hotCards included
    socket.on("stateUpdated", (state) => {
      setRoomState(state);
    });

    return () => {
      socket.off("draftStarted");
      socket.off("stateUpdated");
    };
  }, [socket]);

  useEffect(() => {
    if (!socket) return;
    socket.on("draftComplete", (data) => {
      alert("Draft complete!");
      setDraftComplete(true);
      setFinalResults(data);
      console.log(data)
      setRoomState((prev) => ({ ...prev, draftedCards: data.draftedCards }));
    });
    return () => socket.off("draftComplete");
  }, [socket]);

  return (
    <div className="app-wrapper">
      {draftComplete ? (
        // 🏁 DRAFT COMPLETE SCREEN
        <div
          className="styled-info-modal"
          style={{ textAlign: "center", marginTop: "40px" }}
        >
          <h1 className="header-title">Draft Complete!</h1>
          <p style={{ color: "var(--snap-gold)" }}>
            Saved as <b>{finalResults?.draftName}</b>
          </p>

          {/* 🔥 Hot Cards */}
          {finalResults?.hotCards?.length > 0 && (
            <div style={{ marginTop: "25px" }}>
              <h3 className="header-title" style={{ fontSize: "1.2rem" }}>
                🔥 Hot Cards
              </h3>
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "center",
                  flexWrap: "wrap",
                  marginTop: "10px",
                }}
              >
                {finalResults.hotCards.map((c, i) => (
                  <img
                    key={i}
                    src={c.cardhtml || c.cardHTML}
                    alt={c.cardname || c.cardName}
                    title={c.cardname || c.cardName}
                    style={{
                      width: "110px",
                      borderRadius: "6px",
                      border: "2px solid var(--snap-gold)",
                      boxShadow: "0 0 12px var(--snap-gold)",
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          <p style={{ color: "var(--snap-gold)" }}>
            Saved as <b>{finalResults?.draftName}</b>
          </p>
          <p style={{ color: "#aaa" }}>
            Decks per player: <b>{finalResults?.numDecks}</b> | 
            Card usage limit: <b>{finalResults?.usageLimit}</b>
          </p>

          {/* 🧩 Final Picks */}
          <h3
            className="header-title"
            style={{ fontSize: "1.4rem", marginTop: "40px" }}
          >
            Final Picks
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: "12px",
              justifyItems: "center",
              marginTop: "15px",
            }}
          >
            {finalResults?.draftedCards
              ?.filter((c) => {
                const cardUser =
                  (c.username || c.userName || "").toLowerCase().trim();
                const currentUser =
                  (user.username || user.userName || "").toLowerCase().trim();
                return cardUser === currentUser;
              }).map((c, i) => (
              <div key={i} className="card-snap">
                <img
                  src={c.cardhtml || c.cardHTML}
                  alt={c.cardname || c.cardName}
                  title={`${c.userName} - ${c.cardName}`}
                  style={{
                    width: "100px",
                    borderRadius: "6px",
                    paddingTop: "20px",
                    transform: "scale(1.45)",
                  }}
                />
                <p
                  style={{
                    fontSize: "0.8rem",
                    color: "var(--snap-gold)",
                    marginTop: "4px",
                  }}
                >
                  {c.userName}
                </p>
              </div>
            ))}
          </div>

          {/* 🏠 Return button */}
          <button
            className="modern-btn"
            style={{ marginTop: "35px", padding: "10px 30px" }}
            onClick={() => navigate("/")}
          >
            Return to Home
          </button>
          <button
            className="modern-btn"
            style={{
              marginTop: "20px",
              padding: "10px 30px",
              background: "var(--snap-gold)",
              color: "#000",
            }}
            onClick={() =>
              navigate(`/draft-decks/${finalResults?.draftId}`, {
                state: {
                  draftId: finalResults?.draftId,
                  draftName: finalResults?.draftName,
                  draftedCards: finalResults?.draftedCards,
                  numDecks: finalResults?.numDecks,
                  usageLimit: finalResults?.usageLimit,
                  user,
                },
              })
            }
          >
            Start Creating Decks →
          </button>
        </div>
      ) : (
        // 🟡 LIVE DRAFT SCREEN
        <>
          <h1 className="main-title">Live Draft — Room {roomId}</h1>
          <br /><br /><br />
          {/* Participants */}
          <div
            className="styled-info-modal"
            style={{ maxWidth: "900px", margin: "20px auto" }}
          >
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                justifyContent: "center",
              }}
            >
              {roomState.participants.map((p) => {
                const isCurrent = roomState.currentUser?.id === p.id;
                return (
                  <span
                    key={p.id}
                    className="status-box"
                    style={{
                      background: isCurrent
                        ? "var(--snap-gold)"
                        : "var(--snap-dark)",
                      color: isCurrent ? "#000" : "var(--snap-gold)",
                      border: isCurrent
                        ? "2px solid var(--snap-gold)"
                        : "1px solid var(--snap-muted)",
                      transform: isCurrent ? "scale(1.1)" : "scale(1)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {p.username || p.userName}
                    {isCurrent && " ⭐"}
                  </span>
                );
              })}
            </div>
          </div>

          

          {/* Draft Results by User */}
          <div className="results-container">
            <h2 className="results-heading">Draft Results</h2>

            {roomState.participants.length === 0 && (
              <p style={{ color: "var(--snap-muted)" }}>
                Waiting for participants...
              </p>
            )}

            {roomState.participants.map((p) => {
              const userPicks = roomState.draftedCards.filter(
                (pick) => pick.userid === p.id || pick.userId === p.id
              );

              return (
                <div
                  key={p.id}
                  className="user-block"
                  style={{ marginBottom: "20px" }}
                >
                  <h3
                    style={{
                      color: "var(--snap-gold)",
                      borderBottom: "1px solid var(--snap-gold)",
                      paddingBottom: "5px",
                      marginBottom: "10px",
                    }}
                  >
                    {p.username || p.userName}
                    {roomState.currentUser?.id === p.id && " ⭐"}
                  </h3>

                  <div
                    className="card-grid-modern"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      justifyContent: "center",
                    }}
                  >
                    {userPicks.length > 0 ? (
                      userPicks.map((pick) => (
                        <div
                          key={pick.draftedid || pick.cardid}
                          className="draft-card-modern"
                          onContextMenu={(e) => {
                            e.preventDefault();
                            // Only allow host or currentUser to undo for now
                            if (
                              socket &&
                              (user.id === roomState.currentUser?.id ||
                                user.id === roomState.participants[0]?.id)
                            ) {
                              if (window.confirm(`Undo ${pick.cardname}?`)) {
                                socket.emit("undoPick", { roomId });
                              }
                            }
                          }}
                        >
                          <img
                            src={pick.cardhtml || pick.cardHTML}
                            alt={pick.cardname || pick.cardName}
                            style={{
                              width: "90px",
                              borderRadius: "6px",
                              border: roomState.hotCards?.some(
                                (h) => h.id === pick.cardid
                              )
                                ? "2px solid var(--snap-gold)"
                                : "1px solid rgba(255,255,255,0.1)",
                            }}
                          />
                        </div>
                      ))
                    ) : (
                      <p
                        style={{
                          fontSize: "0.8rem",
                          color: "var(--snap-muted)",
                        }}
                      >
                        No picks yet
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pick interface */}
          <MakeDraftPick
            roomId={roomId}
            user={user}
            socket={socket}
            availableCards={roomState.availableCards}
            currentUser={roomState.currentUser}
            hotCards={roomState.hotCards}
          />
        </>
      )}
    </div>
  );
}
